/**
 * Portal mirror of staff-api `genericRestAdapterConfigSchema` and
 * `setIntegrationSchema` (supplier-catalog PUT). The API secret is write-only:
 * it is never part of the stored config and must not be copied into the form.
 */

import { z } from 'zod';

const SECRET_KEYS = new Set(['secret', 'credentialsEncrypted']);

/** Dot-path into a JSON response, e.g. "data.items". */
const jsonPath = z.string().min(1).max(200);

export const AUTH_TYPES = ['none', 'bearer', 'header', 'query'] as const;
export type SupplierAuthType = (typeof AUTH_TYPES)[number];

export function isSupplierAuthType(value: string): value is SupplierAuthType {
  return (AUTH_TYPES as readonly string[]).includes(value);
}

export const authConfigSchema = z.object({
  type: z.enum(AUTH_TYPES).default('none'),
  /** Header name (type `header`) or query-param name (type `query`). */
  name: z.string().max(100).optional(),
});

export const operationConfigSchema = z.object({
  method: z.enum(['GET', 'POST']).default('GET'),
  path: z.string().min(1).max(500),
  query: z.record(z.string()).optional(),
  body: z.record(z.string()).optional(),
  itemsPath: jsonPath.optional(),
  fields: z.record(jsonPath).default({}),
});

export const SUPPLIER_OPERATION_KEYS = ['search', 'availability', 'price', 'order', 'orderStatus'] as const;
export type SupplierOperationKey = (typeof SUPPLIER_OPERATION_KEYS)[number];

export function isSupplierOperationKey(value: string): value is SupplierOperationKey {
  return (SUPPLIER_OPERATION_KEYS as readonly string[]).includes(value);
}

export const genericRestAdapterConfigSchema = z.object({
  baseUrl: z.string().url().max(500),
  auth: authConfigSchema.default({ type: 'none' }),
  currency: z.string().length(3).default('EUR'),
  search: operationConfigSchema.optional(),
  availability: operationConfigSchema.optional(),
  price: operationConfigSchema.optional(),
  order: operationConfigSchema.optional(),
  orderStatus: operationConfigSchema.optional(),
});

export type GenericRestAdapterConfig = z.infer<typeof genericRestAdapterConfigSchema>;

/** Body of PUT /supplier-catalog/:supplierId/integration. `secret` is omitted to leave the stored secret unchanged. */
export const setSupplierIntegrationSchema = z.object({
  provider: z.string().min(1).max(50),
  config: genericRestAdapterConfigSchema,
  secret: z.string().min(1).max(4000).optional(),
});

export type SetSupplierIntegrationBody = z.infer<typeof setSupplierIntegrationSchema>;

export interface SupplierIntegrationUnconfigured {
  configured: false;
  encryptionAvailable: boolean;
}

export interface SupplierIntegrationConfigured {
  configured: true;
  provider: string;
  config: unknown;
  hasCredentials: boolean;
  encryptionAvailable: boolean;
}

export type SupplierIntegrationView = SupplierIntegrationUnconfigured | SupplierIntegrationConfigured;

export interface SupplierIntegrationFormValues {
  provider: string;
  baseUrl: string;
  authType: SupplierAuthType;
  authName: string;
  currency: string;
  /** Always starts empty. A stored secret is never copied in. */
  secret: string;
  operationsJson: string;
}

export type SupplierIntegrationField =
  | 'provider'
  | 'baseUrl'
  | 'authName'
  | 'currency'
  | 'secret'
  | 'operations';

export type SupplierIntegrationFieldErrors = Partial<Record<SupplierIntegrationField, string>>;

export const supplierIntegrationErrorKeys = {
  provider: 'suppliers.integration.validation.provider',
  baseUrl: 'suppliers.integration.validation.baseUrl',
  currency: 'suppliers.integration.validation.currency',
  authName: 'suppliers.integration.validation.authName',
  secret: 'suppliers.integration.validation.secret',
  operations: 'suppliers.integration.validation.operations',
  operationsJson: 'suppliers.integration.validation.operationsJson',
} as const;

/** PUT returns 503 when a secret is sent and ENCRYPTION_KEY is not configured. */
export function isEncryptionKeyUnavailable(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) return false;
  if (error.status !== 503) return false;
  // A transient DB/pool outage is also a 503, marked by the global handler (#2945).
  const data = 'data' in error ? (error as { data?: unknown }).data : undefined;
  const code = typeof data === 'object' && data !== null ? (data as { code?: unknown }).code : undefined;
  return code !== 'temporarily_unavailable';
}

/** Drop secret-bearing keys anywhere in a config tree so they cannot be rendered or saved back. */
export function stripSecretKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((item) => stripSecretKeys(item));
  if (value === null || typeof value !== 'object') return value;
  const result: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_KEYS.has(key)) continue;
    result[key] = stripSecretKeys(child);
  }
  return result;
}

/**
 * Normalize a GET/PUT integration payload. The secret is dropped even if a
 * response incorrectly includes one at the top level or inside `config`.
 */
export function toSupplierIntegrationView(raw: unknown): SupplierIntegrationView | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const encryptionAvailable = row['encryptionAvailable'] === true;
  if (row['configured'] !== true) {
    return { configured: false, encryptionAvailable };
  }
  const provider = row['provider'];
  return {
    configured: true,
    provider: typeof provider === 'string' ? provider : '',
    config: stripSecretKeys(row['config']),
    hasCredentials: row['hasCredentials'] === true,
    encryptionAvailable,
  };
}

export function emptySupplierIntegrationForm(): SupplierIntegrationFormValues {
  return {
    provider: '',
    baseUrl: '',
    authType: 'none',
    authName: '',
    currency: 'EUR',
    secret: '',
    operationsJson: '{}',
  };
}

export function operationsJsonFromConfig(config: unknown): string {
  const stripped = stripSecretKeys(config);
  if (stripped === null || typeof stripped !== 'object' || Array.isArray(stripped)) return '{}';
  const record = stripped as Record<string, unknown>;
  const operations: Record<string, unknown> = {};
  for (const key of SUPPLIER_OPERATION_KEYS) {
    const value = record[key];
    if (value !== undefined) operations[key] = value;
  }
  return JSON.stringify(operations, null, 2);
}

export function formValuesFromIntegration(view: SupplierIntegrationView): SupplierIntegrationFormValues {
  if (!view.configured) return emptySupplierIntegrationForm();
  const stripped = stripSecretKeys(view.config);
  const record =
    stripped !== null && typeof stripped === 'object' && !Array.isArray(stripped)
      ? (stripped as Record<string, unknown>)
      : {};
  const authRaw = record['auth'];
  const auth =
    authRaw !== null && typeof authRaw === 'object' && !Array.isArray(authRaw)
      ? (authRaw as Record<string, unknown>)
      : {};
  const authTypeRaw = auth['type'];
  const authNameRaw = auth['name'];
  const currencyRaw = record['currency'];
  const baseUrlRaw = record['baseUrl'];
  const authType =
    typeof authTypeRaw === 'string' && isSupplierAuthType(authTypeRaw) ? authTypeRaw : 'none';
  return {
    provider: view.provider,
    baseUrl: typeof baseUrlRaw === 'string' ? baseUrlRaw : '',
    authType,
    authName: typeof authNameRaw === 'string' ? authNameRaw : '',
    currency: typeof currencyRaw === 'string' ? currencyRaw : 'EUR',
    secret: '',
    operationsJson: operationsJsonFromConfig(stripped),
  };
}

function assignFieldError(
  errors: SupplierIntegrationFieldErrors,
  field: SupplierIntegrationField,
  key: string,
): void {
  if (errors[field] === undefined) errors[field] = key;
}

function mapSchemaIssues(
  issues: ReadonlyArray<{ path: ReadonlyArray<PropertyKey> }>,
  errors: SupplierIntegrationFieldErrors,
): void {
  for (const issue of issues) {
    const head = issue.path[0];
    const second = issue.path[1];
    if (head === 'provider') {
      assignFieldError(errors, 'provider', supplierIntegrationErrorKeys.provider);
    } else if (head === 'secret') {
      assignFieldError(errors, 'secret', supplierIntegrationErrorKeys.secret);
    } else if (head === 'config' && second === 'baseUrl') {
      assignFieldError(errors, 'baseUrl', supplierIntegrationErrorKeys.baseUrl);
    } else if (head === 'config' && second === 'currency') {
      assignFieldError(errors, 'currency', supplierIntegrationErrorKeys.currency);
    } else if (head === 'config' && second === 'auth') {
      assignFieldError(errors, 'authName', supplierIntegrationErrorKeys.authName);
    } else {
      assignFieldError(errors, 'operations', supplierIntegrationErrorKeys.operations);
    }
  }
}

export function buildSupplierIntegrationRequest(
  form: SupplierIntegrationFormValues,
): { ok: true; body: SetSupplierIntegrationBody } | { ok: false; errors: SupplierIntegrationFieldErrors } {
  const errors: SupplierIntegrationFieldErrors = {};
  let operationsInput: Record<string, unknown> = {};
  let operationsError: string | undefined;

  try {
    const trimmed = form.operationsJson.trim();
    const parsed: unknown = trimmed === '' ? {} : (JSON.parse(trimmed) as unknown);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      operationsError = supplierIntegrationErrorKeys.operationsJson;
    } else {
      operationsInput = parsed as Record<string, unknown>;
    }
  } catch {
    operationsError = supplierIntegrationErrorKeys.operationsJson;
  }

  if (operationsError === undefined) {
    for (const key of Object.keys(operationsInput)) {
      if (!isSupplierOperationKey(key)) {
        operationsError = supplierIntegrationErrorKeys.operations;
        break;
      }
    }
  }
  if (operationsError !== undefined) errors.operations = operationsError;

  const sanitizedOperations = stripSecretKeys(operationsInput);
  const operationRecord =
    sanitizedOperations !== null && typeof sanitizedOperations === 'object' && !Array.isArray(sanitizedOperations)
      ? (sanitizedOperations as Record<string, unknown>)
      : {};

  const authName = form.authName.trim();
  const auth: { type: SupplierAuthType; name?: string } = { type: form.authType };
  if ((form.authType === 'header' || form.authType === 'query') && authName !== '') {
    auth.name = authName;
  }

  const rawConfig: Record<string, unknown> = {
    baseUrl: form.baseUrl.trim(),
    auth,
  };
  const currency = form.currency.trim().toUpperCase();
  if (currency !== '') rawConfig['currency'] = currency;
  if (operationsError !== supplierIntegrationErrorKeys.operationsJson) {
    for (const key of SUPPLIER_OPERATION_KEYS) {
      const value = operationRecord[key];
      if (value !== undefined) rawConfig[key] = value;
    }
  }

  const strippedConfig = stripSecretKeys(rawConfig);
  const secret = form.secret.trim();
  const candidate =
    secret === ''
      ? { provider: form.provider.trim(), config: strippedConfig }
      : { provider: form.provider.trim(), config: strippedConfig, secret };

  const parsed = setSupplierIntegrationSchema.safeParse(candidate);
  if (!parsed.success) mapSchemaIssues(parsed.error.issues, errors);
  if (!parsed.success || Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, body: parsed.data };
}
